import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiPhoneCall, FiWind } from 'react-icons/fi';
import SafeIcon from '../SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'About Us', path: '/about' },
    { name: 'Service Areas', path: '/areas' },
    { name: 'Financing', path: '/financing' },
    { name: 'Maintenance', path: '/maintenance' },
  ];

  return (
    <>
      {/* Top Banner */}
      <div className="bg-accent text-white py-2 px-4 text-center text-sm font-medium z-50 relative">
        <div className="container-custom flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-6">
          <span className="flex items-center gap-2">
            <span className="animate-pulse h-2 w-2 bg-white rounded-full inline-block"></span>
            24/7 Emergency Service Available
          </span>
          <a href="tel:5145550122" className="flex items-center gap-2 font-bold hover:text-primary transition-colors">
            <SafeIcon icon={FiPhoneCall} /> (514) 555-0122(demo)
          </a>
        </div>
      </div>

      {/* Main Navbar */}
      <header 
        className={`sticky top-0 z-40 w-full transition-all duration-300 ${
          scrolled ? 'bg-white shadow-soft py-3' : 'bg-white/95 backdrop-blur-sm py-4 md:py-5'
        }`}
      >
        <div className="container-custom">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group">
              <div className="bg-primary text-white p-2 rounded-lg group-hover:bg-secondary transition-colors">
                <SafeIcon icon={FiWind} className="text-2xl" />
              </div>
              <div className="flex flex-col">
                <span className="font-heading font-bold text-xl md:text-2xl text-primary leading-none tracking-tight">
                  NORTHPEAK
                </span>
                <span className="text-[0.65rem] md:text-xs text-secondary font-semibold uppercase tracking-widest leading-none mt-1">
                  Heating & Cooling
                </span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-8">
              <ul className="flex items-center gap-6">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    <Link 
                      to={link.path}
                      className={`text-sm font-semibold transition-colors hover:text-secondary ${
                        location.pathname === link.path ? 'text-secondary' : 'text-textMain'
                      }`}
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-4 border-l pl-6 border-gray-200">
                <Link to="/quote" className="btn-primary text-sm px-5 py-2.5">
                  Get Free Quote
                </Link>
              </div>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden text-primary p-2 focus:outline-none"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
            >
              <SafeIcon icon={isOpen ? FiX : FiMenu} className="text-2xl" />
            </button>
          </div>
        </div>

        {/* Mobile Nav Overlay */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-gray-100 absolute w-full left-0 top-full shadow-lg overflow-hidden"
            >
              <div className="container-custom py-4 flex flex-col gap-4">
                <ul className="flex flex-col">
                  {navLinks.map((link) => (
                    <li key={link.path}>
                      <Link 
                        to={link.path}
                        className={`block py-3 text-base font-medium border-b border-gray-50 ${
                          location.pathname === link.path ? 'text-secondary' : 'text-textMain'
                        }`}
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col gap-3 pt-2">
                  <a href="tel:5145550122" className="btn-secondary w-full justify-center">
                    <SafeIcon icon={FiPhoneCall} className="mr-2" /> (514) 555-0122
                  </a>
                  <Link to="/quote" className="btn-primary w-full justify-center">
                    Request Free Estimate
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default Navbar;